'use client';

// ─── TypeScript Types ─────────────────────────────────────────────────────────

export type BirdMovementType =
  | 'initial_placement' |'purchase' |'hatch' |'transfer_in' |'transfer_out' |'mortality' |'culling' |'sale' |'return' |'positive_adjustment' |'negative_adjustment' |'reversal' |'other';

export type BirdMovementStatus =
  | 'draft' |'pending_approval' |'approved' |'posted' |'reversed' |'rejected' |'cancelled';

export type MovementDirection = 'in' | 'out' | 'neutral';

export interface BirdMovement {
  id: string;
  organizationId: string;
  movementNumber: string;
  movementType: BirdMovementType;
  movementDate: string;
  farmId: string;
  farmName: string;
  branchId?: string;
  houseId?: string;
  houseName?: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  birdType: string;
  breed: string;
  quantity: number;
  direction: MovementDirection;
  sourceFarmId?: string;
  sourceFarmName?: string;
  sourceHouseId?: string;
  sourceHouseName?: string;
  destinationFarmId?: string;
  destinationFarmName?: string;
  destinationHouseId?: string;
  destinationHouseName?: string;
  populationBefore?: number;
  populationAfter?: number;
  referenceType?: string;
  referenceId?: string;
  reason?: string;
  notes?: string;
  status: BirdMovementStatus;
  recordedBy: string;
  approvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DailyBirdRecord {
  id: string;
  date: string;
  farmId: string;
  farmName: string;
  houseId: string;
  houseName: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  openingPopulation: number;
  birdsAdded: number;
  transfersIn: number;
  transfersOut: number;
  mortality: number;
  culling: number;
  birdsSold: number;
  otherAdditions: number;
  otherReductions: number;
  closingPopulation: number;
  recordedBy: string;
  notes?: string;
  status: 'draft' | 'submitted' | 'approved';
}

export interface FlockPopulationSummary {
  flockId: string;
  flockNumber: string;
  flockName: string;
  birdType: string;
  breed: string;
  farm: string;
  house: string;
  ageDays: number;
  ageWeeks: number;
  initialBirds: number;
  currentBirds: number;
  mortality: number;
  culled: number;
  sold: number;
  transfersIn: number;
  transfersOut: number;
  survivalRate: number;
  mortalityRate: number;
  status: string;
  houseCapacity: number;
  occupancyPct: number;
}

export interface BirdRecordsSummary {
  currentBirds: number;
  openingPopulation: number;
  birdsAdded: number;
  transfersIn: number;
  transfersOut: number;
  mortality: number;
  culled: number;
  birdsSold: number;
  adjustments: number;
  closingPopulation: number;
  survivalRate: number;
  mortalityRate: number;
  currentBirdsChange: number;
  mortalityChange: number;
}

export interface PopulationTrendPoint {
  date: string;
  opening: number;
  additions: number;
  mortality: number;
  culling: number;
  transfers: number;
  sales: number;
  closing: number;
}

export interface BirdAlert {
  id: string;
  type: 'high_mortality' | 'over_capacity' | 'reconciliation_variance' | 'missing_record' | 'unapproved_adjustment' | 'unusual_culling';
  severity: 'warning' | 'danger';
  title: string;
  message: string;
  flockNumber?: string;
  farm?: string;
  house?: string;
  timestamp: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const MOVEMENT_TYPE_LABELS: Record<BirdMovementType, string> = {
  initial_placement: 'Initial Placement',
  purchase: 'Purchase / Acquisition',
  hatch: 'Hatch',
  transfer_in: 'Transfer In',
  transfer_out: 'Transfer Out',
  mortality: 'Mortality',
  culling: 'Culling',
  sale: 'Sale',
  return: 'Return',
  positive_adjustment: 'Positive Adjustment',
  negative_adjustment: 'Negative Adjustment',
  reversal: 'Reversal',
  other: 'Other',
};

export const MOVEMENT_STATUS_LABELS: Record<BirdMovementStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  posted: 'Posted',
  reversed: 'Reversed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const MOVEMENT_TYPE_COLORS: Record<BirdMovementType, string> = {
  initial_placement: '#2563eb',
  purchase: '#7c3aed',
  hatch: '#059669',
  transfer_in: '#0891b2',
  transfer_out: '#d97706',
  mortality: '#dc2626',
  culling: '#b91c1c',
  sale: '#9333ea',
  return: '#16a34a',
  positive_adjustment: '#15803d',
  negative_adjustment: '#b45309',
  reversal: '#6b7280',
  other: '#475569',
};

export const BIRD_TYPES = ['Broiler', 'Layer', 'Breeder', 'Local/Indigenous', 'Chick', 'Pullet', 'Cockerel', 'Other'];

export const CULLING_REASONS = [
  'Disease',
  'Injury',
  'Poor Performance',
  'Deformity',
  'Age',
  'Production Decision',
  'Other',
];

export const FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm'];

export const HOUSES_BY_FARM: Record<string, string[]> = {
  'Idudi Poultry Farm': ['House A1', 'House A2', 'House B1', 'Layer House 01', 'Layer House 02', 'Breeder House 01', 'Free Range Area'],
  'Kampala Demo Farm': ['House C1', 'House D2', 'Layer House 03'],
};

// ─── Mock Summary Data ────────────────────────────────────────────────────────

export const MOCK_SUMMARY: BirdRecordsSummary = {
  currentBirds: 31140,
  openingPopulation: 31500,
  birdsAdded: 0,
  transfersIn: 200,
  transfersOut: 200,
  mortality: 312,
  culled: 48,
  birdsSold: 0,
  adjustments: 0,
  closingPopulation: 31140,
  survivalRate: 96.8,
  mortalityRate: 1.2,
  currentBirdsChange: -1.1,
  mortalityChange: 8.3,
};

// ─── Mock Flock Population Summaries ─────────────────────────────────────────

export const MOCK_FLOCK_SUMMARIES: FlockPopulationSummary[] = [
  {
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    farm: 'Idudi Poultry Farm',
    house: 'House A1',
    ageDays: 62,
    ageWeeks: 9,
    initialBirds: 5000,
    currentBirds: 4812,
    mortality: 148,
    culled: 40,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 96.24,
    mortalityRate: 3.76,
    status: 'completed',
    houseCapacity: 6000,
    occupancyPct: 80.2,
  },
  {
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    breed: 'Ross 308',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    ageDays: 44,
    ageWeeks: 6,
    initialBirds: 4800,
    currentBirds: 4614,
    mortality: 156,
    culled: 30,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 96.12,
    mortalityRate: 3.88,
    status: 'active',
    houseCapacity: 5000,
    occupancyPct: 92.3,
  },
  {
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    ageDays: 25,
    ageWeeks: 4,
    initialBirds: 3500,
    currentBirds: 3432,
    mortality: 58,
    culled: 10,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 98.06,
    mortalityRate: 1.94,
    status: 'active',
    houseCapacity: 4000,
    occupancyPct: 85.8,
  },
  {
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    birdType: 'Layer',
    breed: 'Lohmann Brown',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    ageDays: 232,
    ageWeeks: 33,
    initialBirds: 8000,
    currentBirds: 7742,
    mortality: 218,
    culled: 40,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 96.77,
    mortalityRate: 3.23,
    status: 'active',
    houseCapacity: 9000,
    occupancyPct: 86.0,
  },
  {
    flockId: 'flock-005',
    flockNumber: 'LY-2026-002',
    flockName: 'Layers Batch L2 — Isa Brown',
    birdType: 'Layer',
    breed: 'ISA Brown',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    ageDays: 196,
    ageWeeks: 28,
    initialBirds: 6500,
    currentBirds: 6318,
    mortality: 162,
    culled: 20,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 97.20,
    mortalityRate: 2.80,
    status: 'active',
    houseCapacity: 7000,
    occupancyPct: 90.3,
  },
  {
    flockId: 'flock-007',
    flockNumber: 'BRD-2026-001',
    flockName: 'Breeders Flock BD1 — Ross',
    birdType: 'Breeder',
    breed: 'Ross Breeder',
    farm: 'Idudi Poultry Farm',
    house: 'Breeder House 01',
    ageDays: 178,
    ageWeeks: 25,
    initialBirds: 2400,
    currentBirds: 2358,
    mortality: 34,
    culled: 8,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 98.25,
    mortalityRate: 1.75,
    status: 'active',
    houseCapacity: 2800,
    occupancyPct: 84.2,
  },
  {
    flockId: 'flock-008',
    flockNumber: 'BR-2026-004',
    flockName: 'Batch C1 — Arbor Acres',
    birdType: 'Broiler',
    breed: 'Arbor Acres',
    farm: 'Kampala Demo Farm',
    house: 'House C1',
    ageDays: 20,
    ageWeeks: 3,
    initialBirds: 6000,
    currentBirds: 5974,
    mortality: 22,
    culled: 4,
    sold: 0,
    transfersIn: 0,
    transfersOut: 0,
    survivalRate: 99.57,
    mortalityRate: 0.43,
    status: 'active',
    houseCapacity: 7000,
    occupancyPct: 85.3,
  },
];

// ─── Mock Bird Movements ──────────────────────────────────────────────────────

export const MOCK_MOVEMENTS: BirdMovement[] = [
  {
    id: 'brm-001',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000001',
    movementType: 'initial_placement',
    movementDate: '2026-08-01',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 5000,
    direction: 'in',
    populationBefore: 0,
    populationAfter: 5000,
    reason: 'Initial flock placement from Ugachick Hatchery',
    referenceType: 'Purchase Order',
    referenceId: 'PO-2026-0142',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-01T08:00:00Z',
    updatedAt: '2026-08-01T09:00:00Z',
  },
  {
    id: 'brm-002',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000002',
    movementType: 'mortality',
    movementDate: '2026-08-05',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 5,
    direction: 'out',
    populationBefore: 5000,
    populationAfter: 4995,
    reason: 'Natural mortality — respiratory infection',
    referenceType: 'Mortality Record',
    referenceId: 'MC-2026-001',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-05T07:30:00Z',
    updatedAt: '2026-08-05T08:00:00Z',
  },
  {
    id: 'brm-003',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000003',
    movementType: 'mortality',
    movementDate: '2026-08-10',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 7,
    direction: 'out',
    populationBefore: 4995,
    populationAfter: 4988,
    reason: 'Natural mortality',
    referenceType: 'Mortality Record',
    referenceId: 'MC-2026-008',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-10T07:30:00Z',
    updatedAt: '2026-08-10T08:00:00Z',
  },
  {
    id: 'brm-004',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000004',
    movementType: 'transfer_out',
    movementDate: '2026-08-15',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 200,
    direction: 'out',
    sourceFarmId: 'farm-idudi',
    sourceFarmName: 'Idudi Poultry Farm',
    sourceHouseId: 'house-a1',
    sourceHouseName: 'House A1',
    destinationFarmId: 'farm-idudi',
    destinationFarmName: 'Idudi Poultry Farm',
    destinationHouseId: 'house-a2',
    destinationHouseName: 'House A2',
    populationBefore: 4988,
    populationAfter: 4788,
    reason: 'Density management — transfer to House A2',
    referenceType: 'Transfer Order',
    referenceId: 'TRF-2026-012',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-15T09:00:00Z',
    updatedAt: '2026-08-15T10:00:00Z',
  },
  {
    id: 'brm-005',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000005',
    movementType: 'sale',
    movementDate: '2026-08-20',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 500,
    direction: 'out',
    populationBefore: 4788,
    populationAfter: 4288,
    reason: 'Partial sale — live birds to Kampala market',
    referenceType: 'Sales Order',
    referenceId: 'SO-2026-0089',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-20T10:00:00Z',
    updatedAt: '2026-08-20T11:00:00Z',
  },
  {
    id: 'brm-006',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000006',
    movementType: 'culling',
    movementDate: '2026-08-22',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a1',
    houseName: 'House A1',
    flockId: 'flock-001',
    flockNumber: 'BR-2026-001',
    flockName: 'Batch A1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 40,
    direction: 'out',
    populationBefore: 4288,
    populationAfter: 4248,
    reason: 'Culling — poor performance and deformity',
    referenceType: 'Culling Record',
    referenceId: 'CUL-2026-004',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-22T08:00:00Z',
    updatedAt: '2026-08-22T09:00:00Z',
  },
  {
    id: 'brm-007',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000007',
    movementType: 'mortality',
    movementDate: '2026-09-01',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-b1',
    houseName: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 18,
    direction: 'out',
    populationBefore: 3450,
    populationAfter: 3432,
    reason: 'Gumboro (IBD) — under investigation',
    referenceType: 'Mortality Record',
    referenceId: 'MC-2026-088',
    status: 'posted',
    recordedBy: 'Ogenga Paul',
    approvedBy: 'Farm Manager',
    createdAt: '2026-09-01T07:00:00Z',
    updatedAt: '2026-09-01T08:00:00Z',
  },
  {
    id: 'brm-008',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000008',
    movementType: 'initial_placement',
    movementDate: '2026-08-10',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-b1',
    houseName: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 3500,
    direction: 'in',
    populationBefore: 0,
    populationAfter: 3500,
    reason: 'Initial placement from Kenchic Uganda',
    referenceType: 'Purchase Order',
    referenceId: 'PO-2026-0178',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-08-10T08:00:00Z',
    updatedAt: '2026-08-10T09:00:00Z',
  },
  {
    id: 'brm-009',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000009',
    movementType: 'positive_adjustment',
    movementDate: '2026-09-02',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a2',
    houseName: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    breed: 'Ross 308',
    quantity: 2,
    direction: 'in',
    populationBefore: 4612,
    populationAfter: 4614,
    reason: 'Reconciliation correction — count discrepancy resolved',
    referenceType: 'Reconciliation',
    referenceId: 'REC-2026-003',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Owner',
    createdAt: '2026-09-02T14:00:00Z',
    updatedAt: '2026-09-02T15:00:00Z',
  },
  {
    id: 'brm-010',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000010',
    movementType: 'mortality',
    movementDate: '2026-09-04',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a2',
    houseName: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    breed: 'Ross 308',
    quantity: 3,
    direction: 'out',
    populationBefore: 4617,
    populationAfter: 4614,
    reason: 'Infectious Bronchitis',
    referenceType: 'Mortality Record',
    referenceId: 'MC-2026-089',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-09-04T07:30:00Z',
    updatedAt: '2026-09-04T08:00:00Z',
  },
  {
    id: 'brm-011',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000011',
    movementType: 'transfer_in',
    movementDate: '2026-09-05',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a2',
    houseName: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    breed: 'Ross 308',
    quantity: 200,
    direction: 'in',
    sourceFarmId: 'farm-idudi',
    sourceFarmName: 'Idudi Poultry Farm',
    sourceHouseId: 'house-a1',
    sourceHouseName: 'House A1',
    destinationFarmId: 'farm-idudi',
    destinationFarmName: 'Idudi Poultry Farm',
    destinationHouseId: 'house-a2',
    destinationHouseName: 'House A2',
    populationBefore: 4414,
    populationAfter: 4614,
    reason: 'Density management — received from House A1',
    referenceType: 'Transfer Order',
    referenceId: 'TRF-2026-012',
    status: 'posted',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    createdAt: '2026-09-05T09:00:00Z',
    updatedAt: '2026-09-05T10:00:00Z',
  },
  {
    id: 'brm-012',
    organizationId: 'org-001',
    movementNumber: 'BRM-2026-000012',
    movementType: 'mortality',
    movementDate: '2026-09-06',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-b1',
    houseName: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 4,
    direction: 'out',
    populationBefore: 3436,
    populationAfter: 3432,
    reason: 'Gumboro (IBD) — continued investigation',
    referenceType: 'Mortality Record',
    referenceId: 'MC-2026-090',
    status: 'pending_approval',
    recordedBy: 'Ogenga Paul',
    createdAt: '2026-09-06T07:00:00Z',
    updatedAt: '2026-09-06T07:00:00Z',
  },
];

// ─── Mock Daily Records ───────────────────────────────────────────────────────

export const MOCK_DAILY_RECORDS: DailyBirdRecord[] = [
  {
    id: 'dr-001',
    date: '2026-09-06',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a2',
    houseName: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    openingPopulation: 4617,
    birdsAdded: 0,
    transfersIn: 0,
    transfersOut: 0,
    mortality: 3,
    culling: 0,
    birdsSold: 0,
    otherAdditions: 0,
    otherReductions: 0,
    closingPopulation: 4614,
    recordedBy: 'James Okello',
    notes: 'Three birds found dead in morning check. Vet notified.',
    status: 'approved',
  },
  {
    id: 'dr-002',
    date: '2026-09-06',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-b1',
    houseName: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    openingPopulation: 3436,
    birdsAdded: 0,
    transfersIn: 0,
    transfersOut: 0,
    mortality: 4,
    culling: 0,
    birdsSold: 0,
    otherAdditions: 0,
    otherReductions: 0,
    closingPopulation: 3432,
    recordedBy: 'Ogenga Paul',
    notes: 'Continued Gumboro investigation. Samples sent to lab.',
    status: 'submitted',
  },
  {
    id: 'dr-003',
    date: '2026-09-05',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'house-a2',
    houseName: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    openingPopulation: 4414,
    birdsAdded: 0,
    transfersIn: 200,
    transfersOut: 0,
    mortality: 0,
    culling: 0,
    birdsSold: 0,
    otherAdditions: 0,
    otherReductions: 0,
    closingPopulation: 4614,
    recordedBy: 'James Okello',
    notes: 'Received 200 birds from House A1 transfer.',
    status: 'approved',
  },
  {
    id: 'dr-004',
    date: '2026-09-06',
    farmId: 'farm-idudi',
    farmName: 'Idudi Poultry Farm',
    houseId: 'layer-house-01',
    houseName: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    openingPopulation: 7744,
    birdsAdded: 0,
    transfersIn: 0,
    transfersOut: 0,
    mortality: 2,
    culling: 0,
    birdsSold: 0,
    otherAdditions: 0,
    otherReductions: 0,
    closingPopulation: 7742,
    recordedBy: 'Sarah Namukasa',
    notes: 'Normal mortality. No disease signs.',
    status: 'approved',
  },
];

// ─── Mock Population Trend ────────────────────────────────────────────────────

export const MOCK_POPULATION_TREND: PopulationTrendPoint[] = [
  { date: '01 Aug', opening: 5000, additions: 0, mortality: 5, culling: 0, transfers: 0, sales: 0, closing: 4995 },
  { date: '05 Aug', opening: 4995, additions: 0, mortality: 5, culling: 0, transfers: 0, sales: 0, closing: 4990 },
  { date: '10 Aug', opening: 4990, additions: 0, mortality: 7, culling: 0, transfers: 0, sales: 0, closing: 4983 },
  { date: '15 Aug', opening: 4983, additions: 0, mortality: 3, culling: 0, transfers: 200, sales: 0, closing: 4780 },
  { date: '20 Aug', opening: 4780, additions: 0, mortality: 4, culling: 0, transfers: 0, sales: 500, closing: 4276 },
  { date: '22 Aug', opening: 4276, additions: 0, mortality: 2, culling: 40, transfers: 0, sales: 0, closing: 4234 },
  { date: '25 Aug', opening: 4234, additions: 0, mortality: 5, culling: 0, transfers: 0, sales: 0, closing: 4229 },
  { date: '01 Sep', opening: 4229, additions: 0, mortality: 8, culling: 0, transfers: 0, sales: 0, closing: 4221 },
  { date: '04 Sep', opening: 4221, additions: 0, mortality: 3, culling: 0, transfers: 0, sales: 0, closing: 4218 },
  { date: '06 Sep', opening: 4218, additions: 0, mortality: 4, culling: 0, transfers: 0, sales: 0, closing: 4214 },
];

// ─── Mock Alerts ──────────────────────────────────────────────────────────────

export const MOCK_ALERTS: BirdAlert[] = [
  {
    id: 'alert-001',
    type: 'high_mortality',
    severity: 'danger',
    title: 'High Mortality Alert',
    message: 'Flock BR-2026-003 recorded mortality above its configured daily threshold (0.524% vs 0.5% limit).',
    flockNumber: 'BR-2026-003',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    timestamp: '2026-09-06T07:30:00Z',
  },
  {
    id: 'alert-002',
    type: 'over_capacity',
    severity: 'warning',
    title: 'Near Capacity Warning',
    message: 'House A2 is at 92.3% occupancy (4,614 / 5,000 birds). Consider density management.',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    timestamp: '2026-09-06T06:00:00Z',
  },
  {
    id: 'alert-003',
    type: 'missing_record',
    severity: 'warning',
    title: 'Missing Daily Record',
    message: 'No daily bird record submitted for Layer House 02 (LY-2026-002) for 06 September 2026.',
    flockNumber: 'LY-2026-002',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    timestamp: '2026-09-06T08:00:00Z',
  },
];

// ─── Flock Population History (Ledger) ───────────────────────────────────────

export interface FlockLedgerEntry {
  date: string;
  movementType: BirdMovementType;
  movementNumber: string;
  description: string;
  quantity: number;
  direction: MovementDirection;
  balance: number;
  reference?: string;
  recordedBy: string;
  status: BirdMovementStatus;
}

export const FLOCK_BR2026001_LEDGER: FlockLedgerEntry[] = [
  { date: '2026-08-01', movementType: 'initial_placement', movementNumber: 'BRM-2026-000001', description: 'Initial Placement — Ugachick Hatchery', quantity: 5000, direction: 'in', balance: 5000, reference: 'PO-2026-0142', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-05', movementType: 'mortality', movementNumber: 'BRM-2026-000002', description: 'Mortality — Respiratory Infection', quantity: 5, direction: 'out', balance: 4995, reference: 'MC-2026-001', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-10', movementType: 'mortality', movementNumber: 'BRM-2026-000003', description: 'Mortality — Natural', quantity: 7, direction: 'out', balance: 4988, reference: 'MC-2026-008', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-15', movementType: 'transfer_out', movementNumber: 'BRM-2026-000004', description: 'Transfer Out → House A2 (density management)', quantity: 200, direction: 'out', balance: 4788, reference: 'TRF-2026-012', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-20', movementType: 'sale', movementNumber: 'BRM-2026-000005', description: 'Sale — Live birds to Kampala market', quantity: 500, direction: 'out', balance: 4288, reference: 'SO-2026-0089', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-22', movementType: 'culling', movementNumber: 'BRM-2026-000006', description: 'Culling — Poor performance and deformity', quantity: 40, direction: 'out', balance: 4248, reference: 'CUL-2026-004', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-08-25', movementType: 'mortality', movementNumber: 'BRM-2026-000013', description: 'Mortality — Natural', quantity: 5, direction: 'out', balance: 4243, reference: 'MC-2026-045', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-09-01', movementType: 'mortality', movementNumber: 'BRM-2026-000014', description: 'Mortality — Heat stress', quantity: 8, direction: 'out', balance: 4235, reference: 'MC-2026-072', recordedBy: 'James Okello', status: 'posted' },
  { date: '2026-09-04', movementType: 'mortality', movementNumber: 'BRM-2026-000015', description: 'Mortality — Natural', quantity: 3, direction: 'out', balance: 4232, reference: 'MC-2026-089', recordedBy: 'James Okello', status: 'posted' },
];
